// Перемотка картины сноса по дням. Расчёта здесь нет: прошлое уже посчитано
// движком и лежит в треке посуточно, будущее — ровно настолько, насколько хватило
// прогнозных течений. Мы только режем готовый трек по дате.
//
// См. docs/plans/2026-07-20-drift-day-slider-design.md
import type { DriftCollection, DriftProps, DriftStatusKind } from '../api'
import { getLang } from '../i18n'

import { DAY_MAX, DAY_MIN, type DayRange } from '../domain/limits'

/** Диапазон перемотки без ограничения домена: неделю назад, два дня вперёд.
 *  Домен вправе сузить его своим окном (`domain/limits.ts::dayRange`). */
export { DAY_MIN, DAY_MAX }

const FULL_RANGE: DayRange = { min: DAY_MIN, max: DAY_MAX }

const DAY_MS = 86_400_000

/** 'YYYY-MM-DD' → мс UTC. UTC, а не локальное время: иначе переход на летнее
 *  время сделал бы разницу двух дат равной 23 или 25 часам и сдвинул индекс. */
function ms(date: string): number {
  const [y, m, d] = date.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

function iso(msUtc: number): string {
  return new Date(msUtc).toISOString().slice(0, 10)
}

/** Разница в сутках между датами (может быть отрицательной). */
export function daysBetween(from: string, to: string): number {
  return Math.round((ms(to) - ms(from)) / DAY_MS)
}

/** Смещение выбранной даты от сегодня, в сутках. */
export function dayOffset(date: string, today: string): number {
  return daysBetween(today, date)
}

/** Смещение в сутках → дата. */
export function dateFromOffset(offset: number, today: string): string {
  return iso(ms(today) + offset * DAY_MS)
}

const DAY_MONTH = new Map<string, Intl.DateTimeFormat>()

/** 'YYYY-MM-DD' → «18 июля» / «18 July». День и месяц без года: подпись стоит
 *  рядом с ползунком недельного окна, и год в ней был бы шумом.
 *
 *  timeZone UTC обязателен: дата уже «календарная», локальная зона сдвинула бы
 *  её на сутки. */
export function humanDate(date: string): string {
  const lang = getLang()
  let fmt = DAY_MONTH.get(lang)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long', timeZone: 'UTC' })
    DAY_MONTH.set(lang, fmt)
  }
  return fmt.format(new Date(ms(date)))
}

export function inRange(offset: number, range: DayRange = FULL_RANGE): boolean {
  return offset >= range.min && offset <= range.max
}

export function clampOffset(offset: number, range: DayRange = FULL_RANGE): number {
  return Math.min(range.max, Math.max(range.min, offset))
}

export interface DriftSlice {
  /** Индекс дня в треке (прижат к последней точке, если день позже конца). */
  index: number
  position: [number, number]
  /** Выбранный день позже конца трека: показываем последнюю известную позицию. */
  frozen: boolean
}

/** Позиция косяка на дату. null — посчитанной позиции на эту дату нет.
 *
 * Индекс считается ОТ КОНЦА трека: `last_date` по построению (`_build_rows`)
 * это дата ПОСЛЕДНЕЙ точки. От `observed_date` считать нельзя — трек начинается
 * с первого дня, на который вообще есть течения, а он может быть сильно позже
 * отметки. На проде так и есть: отметка 11 марта, суточные течения только
 * с 18 мая → трек из 66 точек вместо 134, и индексация от наблюдения улетала
 * за край массива (точка «замерзала» и не двигалась при перемотке).
 */
export function sliceTrack(
  coords: [number, number][],
  lastDate: string,
  date: string,
): DriftSlice | null {
  if (coords.length === 0) return null
  const last = coords.length - 1
  const index = last - daysBetween(date, lastDate)
  if (index < 0) return null // раньше начала трека — позиции не считали
  if (index > last) return { index: last, position: coords[last], frozen: true }
  return { index, position: coords[index], frozen: false }
}

/** Трек → пройденная часть (сплошная) и предстоящая (пунктир).
 *  Точка стыка входит в обе, иначе между линиями будет разрыв.
 *  Отрезок из одной точки — не линия, отдаём пустой. */
export function splitTrack(
  coords: [number, number][],
  index: number,
): { passed: [number, number][]; ahead: [number, number][] } {
  const i = Math.min(Math.max(index, 0), coords.length - 1)
  const passed = coords.slice(0, i + 1)
  const ahead = coords.slice(i)
  return {
    passed: passed.length >= 2 ? passed : [],
    ahead: ahead.length >= 2 ? ahead : [],
  }
}

// ---------------------------------------------------------------------------
// Срез готовой коллекции сноса на выбранный день
// ---------------------------------------------------------------------------

type DriftFeature = DriftCollection['features'][number]

/** Ключ связи трека и позиции. `ref` уникален только В ПРЕДЕЛАХ source
 *  (косяк и вылов — независимые id-последовательности), поэтому источник в ключе. */
function keyOf(p: DriftProps): string {
  return `${p.source}:${p.ref}`
}

/** Коллекцию «как есть» (треки целиком + финальные позиции) → картина на дату.
 *
 * Запроса к серверу не требует: прошлое уже посчитано движком и лежит в треке
 * посуточно. Правила — из §5 дизайна: раньше отметки не показываем вовсе,
 * позже конца трека показываем последнюю известную точку как `frozen`.
 *
 * `today` задаёт ЯКОРЬ: при перемотке рисуются два маркера (сегодняшний
 * приглушённый и дневной яркий), а трек режется на акцентный отрезок между ними
 * и бледный контекст. Начертание (`phase`) отсчитывается ОТ СЕГОДНЯ — сплошная
 * это факт, пунктир прогноз, независимо от положения ползунка.
 *
 * `meta` описывает НАБОР, а не день, поэтому переносится без изменений.
 */
export function sliceDriftCollection(
  fc: DriftCollection,
  date: string,
  today: string = date,
  /** Доля суток [0,1) — нужна ЗАПИСИ ролика, экран всегда стоит на нуле.
   *
   *  Течения суточные (реанализа мельче суток не существует), посчитанных
   *  положений ровно по одному на дату. Ролик идёт плавно, поэтому между двумя
   *  посчитанными узлами интерполируются положение и радиус ореола — и только
   *  они: часов в данных нет, и таймер их не пишет. */
  frac: number = 0,
): DriftCollection {
  // Позиция есть у каждой точки, а LineString — только у треков длиной ≥ 2,
  // поэтому идём от позиций, а треки подтягиваем по ключу.
  const tracks = new Map<string, [number, number][]>()
  for (const f of fc.features) {
    if (f.geometry.type === 'LineString') {
      tracks.set(keyOf(f.properties), f.geometry.coordinates as [number, number][])
    }
  }

  const out: DriftFeature[] = []
  for (const f of fc.features) {
    if (f.geometry.type !== 'Point') continue
    const p = f.properties
    const coords =
      tracks.get(keyOf(p)) ?? [f.geometry.coordinates as [number, number]]

    // Якорь индексации — конец трека (см. sliceTrack), а НЕ дата наблюдения.
    const lastDate = p.last_date ?? p.observed_date
    const day = sliceTrack(coords, lastDate, date)
    if (!day) continue // до начала трека позиции не считали — не рисуем ничего

    // Якорь «сегодня». Его нет, когда ползунок на нуле (индексы совпали) или
    // когда трек начинается позже сегодня — тогда картина одноточечная, как и была.
    const now = sliceTrack(coords, lastDate, today)
    const iDay = day.index
    const iNow = now ? now.index : iDay
    const anchored = now != null && iNow !== iDay

    // Куски трека. Резы — в ОБОИХ индексах, поэтому ни один кусок не пересекает
    // сегодня: фаза куска определяется его началом (см. §1 дизайна).
    const lo = Math.min(iDay, iNow)
    const hi = Math.max(iDay, iNow)
    const parts: { coords: [number, number][]; start: number; segment: 'accent' | 'context' }[] =
      anchored
        ? [
            { coords: coords.slice(0, lo + 1), start: 0, segment: 'context' },
            { coords: coords.slice(lo, hi + 1), start: lo, segment: 'accent' },
            { coords: coords.slice(hi), start: hi, segment: 'context' },
          ]
        : // Ползунок на нуле — трек делится ровно надвое (факт/прогноз), как и до
          // появления якоря: оба куска полноценные, бледнить нечего.
          [
            { coords: coords.slice(0, iDay + 1), start: 0, segment: 'accent' },
            { coords: coords.slice(iDay), start: iDay, segment: 'accent' },
          ]

    // Возраст (им управляется затухание) считается от отметки до выбранного дня.
    const common = { ...p, age_days: daysBetween(p.observed_date, date), frozen: day.frozen }

    for (const part of parts) {
      if (part.coords.length < 2) continue // отрезок из одной точки — не линия
      out.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: part.coords },
        properties: {
          ...common,
          kind: 'track',
          segment: part.segment,
          phase: part.start >= iNow ? 'forecast' : 'fact',
        },
      })
    }

    // Радиус ореола на нужный день: uncertainty_km выровнен с координатами трека,
    // берём по тому же индексу, что и позиция дня.
    //
    // Круг на карте ОДИН — погрешность положения ПОКАЗАННОЙ точки, — и слагаемых
    // у неё два: разброс ансамбля сноса и (когда координаты огрублены) смещение
    // показанного центра относительно истинного. Складываем квадратично:
    // слагаемые независимы. Без ансамбля радиус равен погрешности места — те 112
    // треков из 1087, что при огрублении не показывали ничего (замер 13.08.2026),
    // теперь показывают круг. Без огрубления `place_error_km` нет, и радиус
    // остаётся ровно прежним; нет ни того ни другого — ореола нет.
    const radiusAt = (idx: number): number | null => {
      const u = p.uncertainty_km
      const sigma = u && u.length ? u[Math.min(Math.max(idx, 0), u.length - 1)] : null
      const place = p.place_error_km
      if (sigma == null) return place ?? null
      if (place == null) return sigma
      return Math.hypot(sigma, place)
    }

    // Положение и ореол ВНУТРИ суток. Интерполируем только там, где есть куда
    // ехать: у замёрзшей точки и на последнем узле трека следующего дня нет, и
    // придуманное движение за краем посчитанного было бы враньём.
    const between = frac > 0 && !day.frozen && iDay + 1 < coords.length
    const lerp = (a: number, b: number) => a + (b - a) * frac
    const dayPosition: [number, number] = between
      ? [lerp(coords[iDay][0], coords[iDay + 1][0]), lerp(coords[iDay][1], coords[iDay + 1][1])]
      : day.position
    const dayRadius = (): number | null => {
      const here = radiusAt(iDay)
      if (!between) return here
      const next = radiusAt(iDay + 1)
      if (here == null || next == null) return here
      return lerp(here, next)
    }

    // Отметка происхождения — САМОЙ первой: дневной маркер и якорь ложатся сверху.
    //
    // Берём `origin` (место регистрации), а НЕ первую точку трека: трек начинается
    // с первого дня, на который есть течения, и при отметке 11 марта против течений
    // с 18 мая он стартует за сотни километров от неё.
    //
    // Механизм общий для обоих источников. Раньше отметку рисовал отдельный слой
    // `clusters` — только для косяков, поэтому у факта вылова на карте оставалась
    // одна точка посреди трека, и «откуда снесло» было не видно.
    const origin = p.origin
    const originShown =
      origin != null &&
      !samePoint(origin, dayPosition) &&
      !(anchored && now != null && samePoint(origin, now.position))
    if (originShown) {
      out.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: origin },
        // frozen НЕ наследуем: он про «на выбранный день позиции не считали» и
        // рисуется нулевой заливкой. Отметка — свершившийся факт, замёрзнуть
        // ей нечем, иначе она пропадала бы ровно тогда, когда нужнее всего.
        properties: {
          ...common, kind: 'position', role: 'origin', frozen: false, radius_km: null,
        },
      })
    }

    // Якорь кладём ПЕРЕД дневным маркером: при наложении дневной окажется сверху.
    // Совпали координаты (оба замёрзли в одной точке) — рисуем один маркер.
    if (anchored && now && !samePoint(now.position, dayPosition)) {
      out.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: now.position },
        properties: {
          ...p,
          age_days: daysBetween(p.observed_date, today),
          frozen: now.frozen,
          kind: 'position',
          role: 'today',
          radius_km: radiusAt(iNow),
        },
      })
    }

    out.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: dayPosition },
      properties: { ...common, kind: 'position', role: 'day', radius_km: dayRadius() },
    })
  }

  return { ...fc, features: out }
}

function samePoint(a: [number, number], b: [number, number]): boolean {
  return a[0] === b[0] && a[1] === b[1]
}

/** Дата, которой соответствует ПОД КУРСОРОМ стоящая точка сноса.
 *
 * У одного трека три маркера с разными координатами и разным смыслом даты:
 *   origin — место регистрации, дата НАБЛЮДЕНИЯ;
 *   today  — где косяк сейчас, дата СЕГОДНЯШНЯЯ;
 *   day    — где он на выбранный день, дата ПОЛЗУНКА.
 *
 * Пока карточка печатала выбранный день всем троим, «положение на 12 августа»
 * стояло и над отметкой вылова недельной давности — снос считался верно, а
 * подпись врала (бой 2026-08-12).
 *
 * Роли нет (фича старого среза) или отметка пришла без даты наблюдения —
 * отдаём выбранный день: это прежнее поведение, и оно не хуже выдуманного.
 */
export function pointDate(
  props: { role?: string; observed_date?: string },
  selected: string,
  today: string,
): string {
  if (props.role === 'origin') return props.observed_date || selected
  if (props.role === 'today') return today
  return selected
}

// ---------------------------------------------------------------------------
// Дрейф одного косяка — для панели правки
// ---------------------------------------------------------------------------

export interface ClusterDrift {
  status: DriftStatusKind
  ageDays: number
  currentLon: number
  currentLat: number
  /** Позиции на выбранный день нет — показана последняя известная. */
  frozen: boolean
}

/** Дрейф косяка из УЖЕ НАРЕЗАННОЙ коллекции: позиция на выбранный день.
 *
 * Брать из сырой коллекции нельзя — там лежит конец трека (последний день, до
 * которого досчитаны течения). Панель тогда показывала бы позицию на послезавтра
 * при ползунке на прошлой неделе.
 *
 * `role === 'day'` — отсекаем и приглушённый якорь «сегодня», и отметку
 * происхождения: обе рисуются той же фичей, но отвечают на другой вопрос.
 * Проверять `!== 'today'` нельзя — отметка кладётся ПЕРВОЙ, и `find` вернул бы
 * координаты наблюдения вместо снесённой позиции. `source === 'cluster'` —
 * ref уникален только в пределах источника (см. keyOf).
 */
export function pickClusterDrift(fc: DriftCollection, clusterId: number): ClusterDrift | null {
  const f = fc.features.find(
    ff =>
      ff.properties.kind === 'position' &&
      ff.properties.source === 'cluster' &&
      ff.properties.ref === clusterId &&
      ff.properties.role === 'day',
  )
  if (!f || f.geometry.type !== 'Point') return null
  const [lon, lat] = f.geometry.coordinates as [number, number]
  return {
    status: f.properties.status,
    ageDays: f.properties.age_days,
    currentLon: lon,
    currentLat: lat,
    frozen: Boolean(f.properties.frozen),
  }
}
